import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import * as Sentry from '@sentry/react'
import type { Encounter } from 'fhir/r4'

/** Request body for POST /fhir/encounter/search (proxied to Epic). */
export interface EncounterSearchRequest {
  accessToken: string
  organizationId: string
  fhirPatientId: string
}

export interface PatientEncounterOption {
  id: string
  label: string
  /** FHIR id of the attending (ATND) Practitioner found in the Encounter participant list, if any. */
  attendingPractitionerId?: string
  /** ``Encounter.participant.individual.display`` for the chosen ATND row (Epic often populates this). */
  attendingPractitionerDisplay?: string
  /** True when the encounter type/class denotes an admission (used to pick a default encounter). */
  isAdmission?: boolean
  /** ``Encounter.identifier[].value`` business identifiers (e.g. Epic CSN), used to match the launch-context CSN. */
  identifierValues?: string[]
}

function extractEncounters(payload: unknown): Encounter[] {
  if (payload == null) return []
  if (Array.isArray(payload)) {
    if (payload.length === 0) return []
    const first = payload[0] as { resourceType?: string }
    if (first?.resourceType === 'Encounter') {
      return payload as Encounter[]
    }
    return []
  }
  if (typeof payload !== 'object') return []
  const o = payload as Record<string, unknown>
  if (o.resourceType === 'Bundle' && Array.isArray(o.entry)) {
    return (o.entry as { resource?: unknown }[])
      .map((e) => e.resource)
      .filter(
        (r): r is Encounter =>
          !!r && typeof r === 'object' && (r as Record<string, unknown>)['resourceType'] === 'Encounter'
      )
  }
  if (Array.isArray(o.encounters)) {
    return extractEncounters(o.encounters)
  }
  return []
}

/** Parse period end/start for sorting; missing or invalid dates sort last. */
function encounterPeriodSortMs(enc: Encounter): number {
  const raw = enc.period?.end || enc.period?.start
  if (!raw) return Number.NEGATIVE_INFINITY
  const ms = new Date(raw).getTime()
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms
}

/** Most recent first (by period end, else start). Tie-break by id so comparators never return NaN (−∞ − −∞). */
export function sortEncountersByMostRecent(list: Encounter[]): Encounter[] {
  return [...list].sort((a, b) => {
    const tb = encounterPeriodSortMs(b)
    const ta = encounterPeriodSortMs(a)
    if (tb > ta) return 1
    if (tb < ta) return -1
    return String(a.id ?? '').localeCompare(String(b.id ?? ''))
  })
}

/**
 * Best-effort Epic Contact Serial Number (CSN) for an encounter. Prefers an
 * identifier whose type/system hints "CSN"; otherwise falls back to the first
 * business identifier value. Returns undefined when the encounter has none.
 */
export function extractEncounterCsn(enc: Encounter): string | undefined {
  const identifiers = enc.identifier ?? []
  const hinted = identifiers.find((i) => {
    const hints = [i.type?.text, ...(i.type?.coding?.flatMap((c) => [c.code, c.display]) ?? []), i.system]
    return hints.some((h) => h?.toLowerCase().includes('csn'))
  })
  const chosen = hinted ?? identifiers[0]
  return chosen?.value?.trim() || undefined
}

/** Combine all Encounter.type entries (text, else first coding display) into one de-duplicated string. */
export function extractEncounterTypeText(enc: Encounter): string {
  const seen = new Set<string>()
  const types: string[] = []
  for (const t of enc.type ?? []) {
    const text = (t.text || t.coding?.find((c) => c.display)?.display || '').trim()
    if (!text) continue
    const key = text.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    types.push(text)
  }
  if (types.length > 0) return types.join(', ')
  return enc.class?.display || enc.class?.code || 'Encounter'
}

export function formatEncounterLabel(enc: Encounter): string {
  const typeText = extractEncounterTypeText(enc)
  const start = enc.period?.start
  const datePart = start
    ? (() => {
        const d = new Date(start)
        return Number.isNaN(d.getTime()) ? start : d.toLocaleDateString(undefined, { dateStyle: 'medium' })
      })()
    : ''
  const csn = extractEncounterCsn(enc)
  const csnPart = csn ? `CSN ${csn}` : ''
  const base = [typeText, datePart, csnPart].filter(Boolean).join(' · ')
  const status = enc.status ? ` (${enc.status})` : ''
  const labeled = `${base}${status}`.trim()
  return labeled || enc.id || 'Encounter'
}

/** Parse a Practitioner id from a FHIR reference string, or return null. */
function practitionerIdFromRef(ref: string | undefined): string | null {
  if (!ref?.trim()) return null
  const trimmed = ref.trim()
  // Absolute URL (Epic): .../Practitioner/{id} or .../practitioner/{id}
  const abs = /\/Practitioner\/([^/?#]+)$/i.exec(trimmed)
  if (abs?.[1]) return abs[1].trim()
  // Relative "Practitioner/{id}" or "practitioner/{id}"
  const segments = trimmed.split('/').filter(Boolean)
  const pi = segments.findIndex((s) => s.toLowerCase() === 'practitioner')
  if (pi >= 0 && pi < segments.length - 1) {
    return segments[pi + 1]?.trim() || null
  }
  return null
}

function participantHasAtndCoding(p: NonNullable<Encounter['participant']>[number]): boolean {
  return Boolean(
    p.type?.some((t) =>
      t.coding?.some((c) => {
        const code = c.code?.trim()
        return code != null && code.toUpperCase() === 'ATND'
      })
    )
  )
}

export type AttendingPractitionerPick = { id: string; display?: string }

/**
 * Resolve the attending (ATND) practitioner from an Encounter's participant list.
 *
 * When multiple ATND participants are present, selection priority is:
 *   1. First participant with no period.end (still active).
 *   2. Otherwise, the participant with the latest period.end.
 *
 * Returns null if no ATND participant with a resolvable Practitioner reference is found.
 */
export function extractAttendingPractitioner(encounter: Encounter): AttendingPractitionerPick | null {
  const atndParticipants = (encounter.participant ?? []).filter(participantHasAtndCoding)

  if (atndParticipants.length === 0) return null

  // Keep only those with a resolvable Practitioner reference.
  const candidates = atndParticipants
    .map((p) => ({
      p,
      id: practitionerIdFromRef(p.individual?.reference),
      display: p.individual?.display?.trim() || undefined,
    }))
    .filter((c): c is { p: (typeof atndParticipants)[0]; id: string; display: string | undefined } => c.id !== null)

  if (candidates.length === 0) return null
  if (candidates.length === 1) return { id: candidates[0].id, display: candidates[0].display }

  // Prefer the first candidate with no period.end (still active).
  const active = candidates.find((c) => !c.p.period?.end)
  if (active) return { id: active.id, display: active.display }

  // All have an end time — pick the one with the latest end.
  const best = candidates.reduce((bestCandidate, cur) => {
    const bestMs = new Date(bestCandidate.p.period!.end!).getTime()
    const curMs = new Date(cur.p.period!.end!).getTime()
    return curMs > bestMs ? cur : bestCandidate
  })
  return { id: best.id, display: best.display }
}

/**
 * Extract the FHIR id of the attending (ATND) practitioner from an Encounter's participant list.
 * @see extractAttendingPractitioner
 */
export function extractAttendingPractitionerId(encounter: Encounter): string | null {
  return extractAttendingPractitioner(encounter)?.id ?? null
}

/** Business identifier values (e.g. Epic CSN) from an Encounter, trimmed and non-empty. */
function extractEncounterIdentifierValues(enc: Encounter): string[] {
  return (enc.identifier ?? []).map((i) => i.value?.trim()).filter((v): v is string => Boolean(v))
}

/**
 * True when the encounter denotes an admission. Epic exposes this as an encounter
 * ``type`` (text/coding), so we scan the type entries (and class as a fallback)
 * for the word "admission" case-insensitively.
 */
export function isAdmissionEncounter(enc: Encounter): boolean {
  const candidates: (string | undefined)[] = []
  for (const t of enc.type ?? []) {
    candidates.push(t.text)
    for (const c of t.coding ?? []) {
      candidates.push(c.display, c.code)
    }
  }
  candidates.push(enc.class?.display, enc.class?.code)
  return candidates.some((c) => c?.toLowerCase().includes('admission'))
}

function mapEncountersToOptions(list: Encounter[]): PatientEncounterOption[] {
  const out: PatientEncounterOption[] = []
  for (const enc of list) {
    const id = enc.id?.trim()
    if (!id) continue
    const attending = extractAttendingPractitioner(enc)
    out.push({
      id,
      label: formatEncounterLabel(enc),
      attendingPractitionerId: attending?.id,
      attendingPractitionerDisplay: attending?.display,
      isAdmission: isAdmissionEncounter(enc),
      identifierValues: extractEncounterIdentifierValues(enc),
    })
  }
  return out
}

/**
 * Pick the default encounter id for the Submit Order dropdown.
 *
 * Decision tree:
 *   1. If a launch-context CSN is present and matches an encounter identifier, use it.
 *   2. Otherwise (options are already sorted most-recent first), use the first
 *      encounter whose type is an admission.
 *   3. Otherwise, fall back to the first (most recent) encounter.
 *
 * Returns '' when there are no options.
 */
export function selectDefaultEncounterId(options: PatientEncounterOption[], contextCsn?: string | null): string {
  if (options.length === 0) return ''
  const csn = contextCsn?.trim()
  if (csn) {
    const byCsn = options.find((o) => o.identifierValues?.some((v) => v === csn))
    if (byCsn) return byCsn.id
  }
  const admission = options.find((o) => o.isAdmission)
  if (admission) return admission.id
  return options[0].id
}

/**
 * Load patient encounters via provider-api (proxied to Epic GET Encounter?patient=).
 * Requires Epic launch context (epicState) for accessToken and organizationId.
 */
export async function searchPatientEncounters(
  epicState: string,
  fhirPatientId: string,
  signal?: AbortSignal
): Promise<PatientEncounterOption[]> {
  const client = EpicAuthClient.getInstance()
  if (!client.hasSession(epicState)) {
    return []
  }
  const accessToken = client.getAccessToken(epicState)
  const organizationId = client.getOrgId(epicState)
  const patientId = fhirPatientId.trim()
  if (!accessToken || !organizationId || !patientId) {
    return []
  }

  const body: EncounterSearchRequest = {
    accessToken,
    organizationId,
    fhirPatientId: patientId,
  }

  const providerApi = ProviderAPIClient.getInstance()
  const response = await providerApi.post(
    '/fhir/encounter/search',
    body,
    { 'Content-Type': 'application/json' },
    true,
    signal
  )

  if (!response.ok) {
    const err = new Error(`Encounter search failed: ${response.status}`)
    Sentry.captureException(err, {
      tags: { operation: 'encounter.search', status: response.status },
    })
    throw err
  }

  const json: unknown = await response.json()
  const encounters = sortEncountersByMostRecent(extractEncounters(json))
  return mapEncountersToOptions(encounters)
}
